<template>
  <div class="container py-3">
    <div class="page-header">
      <h2>{{ $t('customer.title') }}</h2>
      <button class="btn btn-primary" @click="openAddModal">{{ $t('customer.add') }}</button>
    </div>

    <div class="search-wrapper mb-3">
      <span class="search-icon">🔍</span>
      <input v-model="search" type="text" class="form-control" :placeholder="$t('customer.searchPlaceholder')" />
    </div>

    <div class="table-card">
      <table class="premium-table table">
        <thead>
          <tr>
            <th>{{ $t('common.name') }}</th>
            <th>{{ $t('common.email') }}</th>
            <th>{{ $t('common.phone') }}</th>
            <th>{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="customers.length === 0">
            <td colspan="4"><div class="empty-state"><div class="empty-icon">👥</div><p>No customers found</p></div></td>
          </tr>
          <tr v-for="customer in customers" :key="customer.id">
            <td><strong>{{ customer.name }}</strong></td>
            <td>{{ customer.email }}</td>
            <td>{{ customer.phone }}</td>
            <td>
              <button class="btn-action btn-action-warning" @click="openEditModal(customer)">✏️ {{ $t('common.edit') }}</button>
              <button class="btn-action btn-action-danger" @click="deleteCustomer(customer.id)">🗑️ {{ $t('common.delete') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <MainPagination
      v-model:currentPage="currentPage"
      v-model:pageSize="pageSize"
      :total="totalRecords"
    />

    <div class="modal fade" ref="customerModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="saveCustomer">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? $t('customer.edit') : $t('customer.add') }}</h5>
              <button type="button" class="btn-close" @click="hideModal"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">{{ $t('common.name') }}</label>
                <input v-model="form.name" type="text" class="form-control" required />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('common.email') }}</label>
                <input v-model="form.email" type="email" class="form-control" required />
              </div>
              <div class="mb-3">
                <label class="form-label">{{ $t('common.phone') }}</label>
                <input v-model="form.phone" type="text" class="form-control" />
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="hideModal">{{ $t('common.cancel') }}</button>
              <button type="submit" class="btn btn-primary">{{ isEditing ? $t('common.update') : $t('common.add') }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api from '../api/axios'
import * as bootstrap from 'bootstrap'
import { useI18n } from 'vue-i18n'
import MainPagination from '../components/MainPagination.vue'

const { t } = useI18n()


const customers = ref([])
const search = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalRecords = ref(0)
const form = ref({})
const isEditing = ref(false)

const customerModal = ref(null)
let customerModalInstance = null

const fetchCustomers = async () => {
  try {
    const res = await api.get('/customers/', {
      params: {
        page: currentPage.value,
        page_size: pageSize.value,
        search: search.value
      }
    })
    customers.value = res.data.results
    totalRecords.value = res.data.count
  } catch (err) {
    console.error(err)
  }
}

let searchTimeout = null
watch(search, () => {
  currentPage.value = 1
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    fetchCustomers()
  }, 300)
})

watch([currentPage, pageSize], () => {
  fetchCustomers()
})

onMounted(() => {
  fetchCustomers()
  customerModalInstance = new bootstrap.Modal(customerModal.value)
})

const openAddModal = () => {
  isEditing.value = false
  form.value = { name: '', email: '', phone: '' }
  customerModalInstance.show()
}

const openEditModal = (customer) => {
  isEditing.value = true
  form.value = { ...customer }
  customerModalInstance.show()
}

const hideModal = () => customerModalInstance.hide()

const saveCustomer = async () => {
  try {
    if (isEditing.value) {
      await api.put(`/customers/${form.value.id}/`, form.value)
    } else {
      await api.post('/customers/', form.value)
    }
    fetchCustomers()
    hideModal()
  } catch (err) {
    console.error(err)
  }
}

const deleteCustomer = async (id) => {
  if (!confirm(t('customer.confirmDelete'))) return
  try {
    await api.delete(`/customers/${id}/`)
    fetchCustomers()
  } catch (err) {
    console.error(err)
  }
}
</script>

